/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useMemo } from 'react';

import isNil from 'lodash/isNil';
import { graphql, useLazyLoadQuery } from 'react-relay';

import {
	COMPOUND_OPERATOR_AND,
	creators,
	OPERATOR_GT_EQUALS,
	OPERATOR_IN,
	print,
} from '@atlaskit/jql-ast';
import { JiraIssueAri } from '@atlassian/ari/jira/issue';

import type {
	CapacityPlanningDataQuery,
	CapacityPlanningDataQuery$data,
} from './__generated__/CapacityPlanningDataQuery.graphql.js';
import { isCustomContributionTargetARI, isJiraWorkARI } from './capacity-planning-utils';
import type { HydratedCapacityPlanningDataResult, HydratedUser, HydratedWork } from './types';

const HIERARCHY_LEVEL_FIELD = 'hierarchyLevel';
const HIERARCHY_LEVEL_MIN = 1;

type ParsedJiraAri = { siteId: string; issueId: string };

/**
 * Builds JQL to fetch specific issues by id from parsed Jira ARIs.
 * Format: hierarchyLevel >= 1 AND id IN (11064, 11063)
 */
function buildJqlFromParsedJiraAris(
	parsed: ParsedJiraAri[],
): { cloudId: string; jql: string; first: number } | null {
	if (parsed.length === 0) {
		return null;
	}
	const cloudId = parsed[0].siteId;
	const issueIds = parsed.map((p) => p.issueId);
	const idClause = creators.terminalClause(
		creators.field('id'),
		creators.operator(OPERATOR_IN),
		creators.listOperand(issueIds.map(creators.valueOperand)),
	);
	const hierarchyClause = creators.terminalClause(
		creators.field(HIERARCHY_LEVEL_FIELD),
		creators.operator(OPERATOR_GT_EQUALS),
		creators.valueOperand(String(HIERARCHY_LEVEL_MIN)),
	);
	const whereClause = creators.compoundClause(creators.compoundOperator(COMPOUND_OPERATOR_AND), [
		hierarchyClause,
		idClause,
	]);
	const jql = print(creators.jast(creators.query(whereClause)));
	return { cloudId, jql, first: parsed.length };
}

function buildResultFromData(data: CapacityPlanningDataQuery$data | null): {
	usersByAccountId: Map<string, HydratedUser>;
	workById: Map<string, HydratedWork>;
} {
	// Build map of users by account ID
	const usersByAccountId = new Map(
		(data?.users ?? []).map((u) => [u.id, { id: u.id, name: u.name, picture: u.picture }]),
	);

	// Build map of custom contribution targets by ID
	const customContributionTargets = (data?.cpls_customContributionTargetsByIds ?? []).flatMap(
		(item) => {
			const name = item?.name?.trim();
			return name ? [[item!.id, { id: item!.id, name }] as const] : [];
		},
	);

	// Build map of Jira work items by ID
	const jiraWorkItems = (data?.jira?.issueSearch?.edges ?? []).flatMap((edge) => {
		const node = edge?.node;
		const name = node?.summary?.trim();
		const url = node?.webUrl?.trim();
		const hierarchyLevel = node?.issueType?.hierarchy?.level;
		const issueTypeAvatarUrl = node?.issueTypeAvatarUrl?.trim();
		const issueTypeName = node?.issueType?.name?.trim();
		return !isNil(node?.id) && name && url && !isNil(hierarchyLevel)
			? [
					[
						node.id,
						{
							id: node.id,
							name,
							url,
							hierarchyLevel,
							...(issueTypeAvatarUrl ? { issueTypeAvatarUrl, issueTypeName } : {}),
						},
					] as const,
				]
			: [];
	});

	// Build map of all work items by ID
	const workById = new Map<string, HydratedWork>([...customContributionTargets, ...jiraWorkItems]);
	return { usersByAccountId, workById };
}

type CapacityPlanningDataQueryProps = {
	contributorIds: string[] | null | undefined;
	workIds: string[] | null | undefined;
	children: (data: HydratedCapacityPlanningDataResult) => React.ReactNode;
};

export function CapacityPlanningDataQuery({
	contributorIds,
	workIds,
	children,
}: CapacityPlanningDataQueryProps): React.ReactNode {
	// Get account IDs and workIds
	const accountIds = useMemo(() => contributorIds ?? [], [contributorIds]);
	// Parse Jira ARIs to get Jira issue IDs
	const parsedJiraIds = useMemo(
		() =>
			(workIds ?? []).filter(isJiraWorkARI).map((ari) => JiraIssueAri.parse(ari).getVariables()),
		[workIds],
	);
	const customContributionTargetIds = useMemo(
		() => (workIds ?? []).filter(isCustomContributionTargetARI),
		[workIds],
	);

	// Build JQL to fetch Jira work items
	const jiraParams = useMemo(() => buildJqlFromParsedJiraAris(parsedJiraIds), [parsedJiraIds]);
	const includeJiraWorkItems = Boolean(jiraParams?.jql);
	const includeCustomContributionTargets = customContributionTargetIds.length > 0;

	const variables = useMemo(
		() => ({
			accountIds: [...accountIds].sort(),
			customContributionTargetIds: [...customContributionTargetIds].sort(),
			includeJiraWorkItems,
			includeCustomContributionTargets,
			cloudId: jiraParams?.cloudId ?? '',
			jql: jiraParams?.jql,
			first: jiraParams?.first ?? 0,
		}),
		[
			accountIds,
			customContributionTargetIds,
			includeJiraWorkItems,
			includeCustomContributionTargets,
			jiraParams?.cloudId,
			jiraParams?.jql,
			jiraParams?.first,
		],
	);

	const data = useLazyLoadQuery<CapacityPlanningDataQuery>(
		graphql`
			query CapacityPlanningDataQuery(
				$accountIds: [ID!]!
				$customContributionTargetIds: [ID!]!
				$includeJiraWorkItems: Boolean!
				$includeCustomContributionTargets: Boolean!
				$cloudId: ID!
				$jql: String
				$first: Int!
			) {
				users(accountIds: $accountIds) {
					id
					name
					picture
				}
				cpls_customContributionTargetsByIds(ids: $customContributionTargetIds)
					@optIn(to: "Cpls")
					@include(if: $includeCustomContributionTargets) {
					id
					name
				}
				jira {
					issueSearch(cloudId: $cloudId, issueSearchInput: { jql: $jql }, first: $first)
						@include(if: $includeJiraWorkItems)
						@optIn(to: "JiraSpreadsheetComponent-M1") {
						edges {
							node {
								id
								summary
								webUrl
								issueTypeAvatarUrl
								issueType {
									name
									hierarchy {
										level
									}
								}
							}
						}
					}
				}
			}
		`,
		variables,
		{ fetchPolicy: 'store-or-network' },
	);

	const result = useMemo(() => buildResultFromData(data), [data]);

	return children(result);
}
